import React from 'react';
import { Flex, Menu, MenuButton, MenuList, MenuItem, Input, Portal } from '@chakra-ui/react';
import { CaretDown } from 'phosphor-react';

/**
 * Barra de busca com toggle de modo — padrão visual do módulo Auditoria
 * Inteligente (ContextBar): um botão-menu à esquerda (Tudo / Razão / CNPJ / ID)
 * colado a um campo de texto unificado à direita.
 *
 * PRESENTATIONAL e CONTROLADO: não guarda estado nem faz debounce/consulta — a
 * tela dona controla `mode`/`value` e a lógica de filtro. Só padroniza o LOOK,
 * sem mudar o comportamento de busca de cada tela.
 *
 * Desacoplado do módulo Auditoria (não importa `useAuditoriaInteligente`/`UI`) —
 * cores replicadas de `auditoriaInteligente/ui.ts` para reuso em qualquer tela.
 */
const C = {
  border: '#E4E4E7',
  neutralSoft: '#EEF2F6',
  neutralHover: '#E6EBF0',
  muted: '#71717A',
  ink: '#0F172A',
  brand: '#1B88F5',
  infoSoft: '#E9F4FD',
};

export interface SearchModeOption {
  /** chave do modo (ex.: 'tudo', 'cnpj') — o que a tela usa na lógica de filtro. */
  key: string;
  /** texto curto no botão do toggle (ex.: 'Tudo', 'CNPJ'). */
  buttonLabel: string;
  /** texto no item do menu (ex.: 'Tudo (ID · CNPJ · Razão)'). */
  menuLabel: string;
  /** placeholder do campo quando este modo está ativo. */
  placeholder: string;
}

/** Config reutilizável ID/Nome/CNPJ (keys como a maioria das telas usa hoje). */
export const MODES_ID_NOME_CNPJ: SearchModeOption[] = [
  { key: 'ID', buttonLabel: 'ID', menuLabel: 'ID (external_id)', placeholder: 'Digite o ID…' },
  {
    key: 'Nome',
    buttonLabel: 'Nome',
    menuLabel: 'Nome / Razão social',
    placeholder: 'Buscar por nome ou razão social…',
  },
  { key: 'CNPJ', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Digite o CNPJ…' },
];

/** Config ID/Nome/Email (telas de usuários/clientes). */
export const MODES_ID_NOME_EMAIL: SearchModeOption[] = [
  { key: 'ID', buttonLabel: 'ID', menuLabel: 'ID (external_id)', placeholder: 'Digite o ID…' },
  { key: 'Nome', buttonLabel: 'Nome', menuLabel: 'Nome', placeholder: 'Buscar por nome…' },
  { key: 'Email', buttonLabel: 'Email', menuLabel: 'E-mail', placeholder: 'Buscar por e-mail…' },
];

/** Config ID/Nome (telas sem CNPJ — ex.: grupos). */
export const MODES_ID_NOME: SearchModeOption[] = [
  { key: 'ID', buttonLabel: 'ID', menuLabel: 'ID (external_id)', placeholder: 'Digite o ID…' },
  { key: 'Nome', buttonLabel: 'Nome', menuLabel: 'Nome', placeholder: 'Buscar por nome…' },
];

/** Config para filtros de SÓCIOS (Nome/CPF/CNPJ — sem ID). */
export const MODES_NOME_CPF_CNPJ: SearchModeOption[] = [
  {
    key: 'Nome',
    buttonLabel: 'Nome',
    menuLabel: 'Nome / Razão social',
    placeholder: 'Buscar por nome ou razão social…',
  },
  { key: 'CPF', buttonLabel: 'CPF', menuLabel: 'CPF', placeholder: 'Digite o CPF…' },
  { key: 'CNPJ', buttonLabel: 'CNPJ', menuLabel: 'CNPJ', placeholder: 'Digite o CNPJ…' },
];

interface SearchModeBarProps {
  modes: SearchModeOption[];
  mode: string;
  onModeChange: (key: string) => void;
  value: string;
  onValueChange: (value: string) => void;
  height?: string;
  /** largura máx. do bloco (default cresce até 480px em telas médias). */
  maxW?: string | Record<string, string>;
  /** dica de teclado do campo (repassada ao input) — ex.: 'numeric' p/ ID/CNPJ. */
  inputMode?: 'text' | 'numeric' | 'search' | 'tel' | 'none' | 'url' | 'email' | 'decimal';
  /** limite de caracteres do campo (repassado ao input) — ex.: 18 p/ CNPJ mascarado. */
  maxLength?: number;
  /** handler de tecla no campo (repassado ao input) — ex.: buscar no Enter. */
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export function SearchModeBar({
  modes,
  mode,
  onModeChange,
  value,
  onValueChange,
  height = '38px',
  maxW = { base: '100%', md: '480px' },
  inputMode,
  maxLength,
  onKeyDown,
}: SearchModeBarProps) {
  const current = modes.find(m => m.key === mode) ?? modes[0];

  return (
    <Flex
      align="center"
      w={{ base: '100%', sm: 'auto' }}
      maxW={maxW}
      minW={{ base: '100%', sm: '320px' }}
      flex={{ base: '1 1 100%', sm: '0 1 480px' }}
    >
      <Menu placement="bottom-start" autoSelect={false}>
        <MenuButton
          type="button"
          display="inline-flex"
          alignItems="center"
          gap="5px"
          h={height}
          px="11px"
          flexShrink={0}
          borderRadius="9px 0 0 9px"
          border="1px solid"
          borderColor={C.border}
          borderRight="none"
          bg={C.neutralSoft}
          fontSize="12px"
          fontWeight={700}
          color={C.muted}
          whiteSpace="nowrap"
          _hover={{ bg: C.neutralHover, color: C.ink }}
          _active={{ bg: C.neutralHover }}
          // o MenuButton embrulha o conteúdo num span: sem isso a seta quebra
          // para baixo do texto
          sx={{ '& > span': { display: 'inline-flex', alignItems: 'center', gap: '5px' } }}
        >
          {current.buttonLabel}
          <CaretDown size={11} weight="bold" />
        </MenuButton>
        {/* Portal: dentro de card com blur/z-index o menu ficava por baixo do
            conteúdo seguinte e o clique caía no card (Supervisor LP/LR) */}
        <Portal>
          <MenuList minW="210px" py={1} boxShadow="0 8px 24px rgba(20,40,60,.14)" zIndex={1500}>
            {modes.map(m => (
              <MenuItem
                key={m.key}
                onClick={() => onModeChange(m.key)}
                fontSize="13px"
                fontWeight={mode === m.key ? 700 : 500}
                color={mode === m.key ? C.brand : C.ink}
                bg={mode === m.key ? C.infoSoft : undefined}
              >
                {m.menuLabel}
              </MenuItem>
            ))}
          </MenuList>
        </Portal>
      </Menu>
      <Input
        value={value}
        onChange={e => onValueChange(e.target.value)}
        placeholder={current.placeholder}
        inputMode={inputMode}
        maxLength={maxLength}
        onKeyDown={onKeyDown}
        h={height}
        flex={1}
        minW={0}
        borderRadius="0 9px 9px 0"
        borderColor={C.border}
        bg="white"
        fontSize="13px"
        boxShadow="0px 2px 4px #1E293B0D"
        _hover={{ borderColor: C.border }}
        _focusVisible={{ borderColor: C.brand, boxShadow: `0 0 0 1px ${C.brand}` }}
      />
    </Flex>
  );
}
